import React, { useEffect, useState } from 'react';
import { FaTooth, FaBox, FaCalendarAlt, FaTools, FaFlask, FaUser, FaFileMedical, FaXRay, FaCheckCircle } from 'react-icons/fa';

const ImplantForm = ({ implantData, handleImplantChange, handleFileChange, handleFormSubmission }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    handleImplantChange({
      target: {
        name: 'insertionDate',
        value: ''
      }
    });
    handleImplantChange({
      target: {
        name: 'timeToFailure',
        value: '',
        type: 'radio'
      }
    });
    window.scrollTo(0, 0);
  }, [handleImplantChange]);

  const calculateTimeToFailure = (insertionDate) => {
    if (!insertionDate) {
      handleImplantChange({
        target: {
          name: 'timeToFailure',
          value: '',
          type: 'radio'
        },
      });
      return;
    }

    const currentDate = new Date();
    const insertionDateObj = new Date(insertionDate);
    const timeDiff = currentDate - insertionDateObj;

    const days = Math.floor(timeDiff / (1000 * 3600 * 24));
    let timeCategory = '';

    if (days <= 42) {
      timeCategory = '0-6 weeks';
    } else if (days <= 180) {
      timeCategory = '6 weeks - 6 months';
    } else if (days <= 2190) {
      timeCategory = '6 months - 6 years';
    } else {
      timeCategory = '6 years+';
    }

    handleImplantChange({
      target: {
        name: 'timeToFailure',
        type: 'radio',
        value: timeCategory,
        checked: true
      },
    });
  };

  const handleInsertionDateChange = (e) => {
    handleImplantChange(e);
    calculateTimeToFailure(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      handleFormSubmission(e);
      setIsSubmitting(false);
    }, 100);
  };

  // Render checkbox group with modern TailwindPlus styling
  const renderCheckboxGroup = (label, name, options, isSingleSelection = false) => {
    const currentValue = implantData[name];
    const valueArray = Array.isArray(currentValue) 
      ? currentValue 
      : (currentValue && currentValue !== '' ? [currentValue] : []);
    
    return (
      <div className="mt-6">
        <label className="block text-sm/6 font-medium text-gray-900 mb-4">
          {label}
        </label>
        <div className={isSingleSelection ? "flex flex-wrap gap-x-6 gap-y-3" : "grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3"}>
          {options.map((option) => (
            <div key={option} className="flex gap-3">
              <div className="flex h-6 shrink-0 items-center">
                <div className="group grid size-4 grid-cols-1">
                  <input
                    type={isSingleSelection ? 'radio' : 'checkbox'}
                    name={name}
                    value={option}
                    checked={isSingleSelection
                      ? currentValue === option
                      : valueArray.includes(option)}
                    onChange={(e) =>
                      handleImplantChange({
                        target: {
                          name,
                          type: isSingleSelection ? 'radio' : 'checkbox',
                          value: option,
                          checked: e.target.checked
                        },
                      })
                    }
                    className={isSingleSelection 
                      ? "relative size-4 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-indigo-600 checked:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden"
                      : "col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                    }
                  />
                  {!isSingleSelection && (
                    <svg
                      fill="none"
                      viewBox="0 0 14 14"
                      className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white group-has-[:disabled]:stroke-gray-950/25"
                    >
                      <path
                        d="M3 8L6 11L11 3.5"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="opacity-0 group-has-[:checked]:opacity-100"
                      />
                    </svg>
                  )}
                </div>
              </div>
              <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">
                {option}
              </label>
            </div>
          ))}
        </div>
        {valueArray.includes('Other') && (
          <div className="mt-4">
            <input
              type="text"
              name={`${name}Other`}
              placeholder="Specify manufacturer"
              value={implantData[`${name}Other`] || ''}
              onChange={handleImplantChange}
              className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex size-16 items-center justify-center rounded-full bg-orange-100">
            <FaTooth className="size-8 text-orange-600" aria-hidden="true" />
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Dental Implant Complication Form
          </h1>
          <p className="mt-2 text-base text-gray-600">
            Report complications related to dental implant procedures
          </p>
        </div>

        {/* Form Card */}
        <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/5">
          <form onSubmit={handleSubmit}>
            <div className="space-y-12 px-6 py-8 sm:px-10 sm:py-10">
              {/* Implant Manufacturer Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaBox className="size-5 text-gray-400" />
                  Implant Manufacturer
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Select the manufacturer of the implant
                </p>
                {renderCheckboxGroup('Implant Manufacturer:', 'manufacturer', [
                  'Biohorizons', 'Nobel', 'Neodent', 'Straumann', 'Zimmer/ZimVie', 'Astra/Dentsply', 'BlueSky Bio', 'Noris', 'Other'
                ])}
              </div>

              {/* Implant Product Info Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaBox className="size-5 text-gray-400" />
                  Implant Product Information
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Enter product identification details
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-3">
                  <div>
                    <label htmlFor="itemNumber" className="block text-sm/6 font-medium text-gray-900">
                      Item Number
                    </label>
                    <div className="mt-2">
                      <input
                        id="itemNumber"
                        name="itemNumber"
                        type="text"
                        value={implantData.itemNumber || ''}
                        onChange={handleImplantChange}
                        placeholder="Enter item number"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="serialNumber" className="block text-sm/6 font-medium text-gray-900">
                      Serial Number
                    </label>
                    <div className="mt-2">
                      <input
                        id="serialNumber"
                        name="serialNumber"
                        type="text"
                        value={implantData.serialNumber || ''}
                        onChange={handleImplantChange}
                        placeholder="Enter serial number"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="lotNumber" className="block text-sm/6 font-medium text-gray-900">
                      Lot Number
                    </label>
                    <div className="mt-2">
                      <input
                        id="lotNumber"
                        name="lotNumber"
                        type="text"
                        value={implantData.lotNumber || ''}
                        onChange={handleImplantChange}
                        placeholder="Enter lot number"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Insertion Date and Time to Failure Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaCalendarAlt className="size-5 text-gray-400" />
                  Insertion Date & Time to Failure
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Enter insertion date and time to failure information
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="insertionDate" className="block text-sm/6 font-medium text-gray-900">
                      Insertion Date
                    </label>
                    <div className="mt-2">
                      <input
                        id="insertionDate"
                        name="insertionDate"
                        type="date"
                        value={implantData.insertionDate || ''}
                        onChange={handleInsertionDateChange}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>
                {renderCheckboxGroup('Time to Failure:', 'timeToFailure', [
                  '0-6 weeks', '6 weeks - 6 months', '6 months - 6 years', '6 years+',
                ], true)}
              </div>

              {/* Placement Details Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaTools className="size-5 text-gray-400" />
                  Placement Details
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Information about implant placement
                </p>

                {renderCheckboxGroup('Insertion Torque:', 'insertionTorque', ['<20','20-30', '30-40', '>40'])}

                {renderCheckboxGroup('Context of Placement:', 'contextOfPlacement', [
                  'Immediate', 'Delayed', 'Healed Ridge'
                ])}

                {renderCheckboxGroup('Implant Site Tissue Features:', 'tissueFeatures', [
                  'Fenestration', 'Thread exposure', 'Dehiscence', 'Discolored Gingivae', 'Bone loss',
                ])}
              </div>

              {/* Grafting Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaFlask className="size-5 text-gray-400" />
                  Grafting Information
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Grafting type and membrane details
                </p>

                {renderCheckboxGroup('Type of Graft:', 'graftingType', ['Allogeneic', 'Xenogeneic', 'Autograft'])}
                {renderCheckboxGroup('Membrane:', 'membrane', ['Yes', 'No'], true)}
                {implantData.membrane === 'Yes' && (
                  <div className="mt-4">
                    <label htmlFor="membraneType" className="block text-sm/6 font-medium text-gray-900">
                      Membrane Type
                    </label>
                    <div className="mt-2">
                      <input
                        id="membraneType"
                        name="membraneType"
                        type="text"
                        placeholder="Enter membrane type"
                        value={implantData.membraneType || ''}
                        onChange={handleImplantChange}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                )}

                {renderCheckboxGroup('Healing:', 'healing', ['Cover Screw', 'Healing Abutment'])}
              </div>

              {/* Patient Information Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaUser className="size-5 text-gray-400" />
                  Patient Information
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Patient demographics and medical history
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
                  {[
                    { name: 'age', placeholder: 'Age', type: 'number' },
                    { name: 'medicalHistory', placeholder: 'Medical History', type: 'text' },
                    { name: 'surgicalHistory', placeholder: 'Surgical History', type: 'text' },
                    { name: 'radiation', placeholder: 'Radiation', type: 'text' },
                    { name: 'chemotherapy', placeholder: 'Chemotherapy', type: 'text' },
                    { name: 'medication', placeholder: 'Medication', type: 'text' },
                    { name: 'tobaccoUse', placeholder: 'Tobacco use (amount and freq)', type: 'text' },
                    { name: 'alcoholUse', placeholder: 'Alcohol use (amount and freq)', type: 'text' },
                    { name: 'recreationalDrug', placeholder: 'Recreational Drug (amount and freq)', type: 'text' },
                    { name: 'hygiene', placeholder: 'Hygiene', type: 'text' },
                    { name: 'parafunctionalHabits', placeholder: 'Parafunctional Habits', type: 'text' },
                    { name: 'periodontitis', placeholder: 'Periodontitis', type: 'text' },
                    { name: 'gingivitis', placeholder: 'Gingivitis', type: 'text' },
                  ].map(({ name, placeholder, type }) => (
                    <div key={name}>
                      <label htmlFor={name} className="block text-sm/6 font-medium text-gray-900">
                        {placeholder}
                      </label>
                      <div className="mt-2">
                        <input
                          id={name}
                          name={name}
                          type={type}
                          value={implantData[name] || ''}
                          onChange={handleImplantChange}
                          placeholder={placeholder}
                          className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Restoring Practitioner Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaUser className="size-5 text-gray-400" />
                  Restoring Practitioner Issues
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Issues related to the restoring practitioner
                </p>

                {renderCheckboxGroup('Restoring Practitioner:', 'restoringPractitioner', [
                  'Occlusal interference', 'Restoration contours', 'Inappropriate torque',
                  'Inadequate restorative space', 'Injudicious cementation', 'Prosthetic Restoration'
                ])}

                {Array.isArray(implantData.restoringPractitioner) && implantData.restoringPractitioner.includes('Prosthetic Restoration') && (
                  <div className="mt-6">
                    {renderCheckboxGroup('Prosthetic Restoration:', 'prostheticRestoration', [
                      'Cement', 'Screw retained', 'Single', 'FPD', 'Full arch', 'Removable'
                    ], true)}
                  </div>
                )}
              </div>

              {/* Surgeon Issues Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaUser className="size-5 text-gray-400" />
                  Surgeon Issues
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Issues related to the surgeon
                </p>

                {renderCheckboxGroup('Surgeon:', 'surgeonIssues', [
                  'Incorrect angulation', 'Incorrect depth', 'Damage to adjacent teeth', 'Osteomyelitis/Chronic infection',
                  'Infrequent drill replacement', 'Over-torqued implant', 'Primary stability', 'Nerve Injury',
                  'Inadequate keratinized tissue', 'Sinus perforation/exposure', 'Retained root tip/infection'
                ])}
              </div>

              {/* Device Issues Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaBox className="size-5 text-gray-400" />
                  Device Issues
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Issues related to the implant device
                </p>

                {renderCheckboxGroup('Device:', 'deviceIssues', [
                  'Implant Body Failure', 'Restorative Component Failure (abutment screw)', 'Implant Packaging Failure',
                  'Visible Defect in Implant or Components'
                ])}
              </div>

              {/* Surgical Instrumentation Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaTools className="size-5 text-gray-400" />
                  Surgical Instrumentation
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Sterilization and drill replacement information
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="sterilizationMethod" className="block text-sm/6 font-medium text-gray-900">
                      Method of Sterilization
                    </label>
                    <div className="mt-2">
                      <input
                        id="sterilizationMethod"
                        name="sterilizationMethod"
                        type="text"
                        value={implantData.sterilizationMethod || ''}
                        onChange={handleImplantChange}
                        placeholder="Enter sterilization method"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="cyclesBeforeReplacement" className="block text-sm/6 font-medium text-gray-900">
                      Number of Cycles Before Drill Replacement
                    </label>
                    <div className="mt-2">
                      <input
                        id="cyclesBeforeReplacement"
                        name="cyclesBeforeReplacement"
                        type="number"
                        value={implantData.cyclesBeforeReplacement || ''}
                        onChange={handleImplantChange}
                        placeholder="Enter number of cycles"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Diagnosis at Failure Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaFileMedical className="size-5 text-gray-400" />
                  Diagnosis at Failure
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Select the diagnosis at time of failure
                </p>

                {renderCheckboxGroup('Diagnosis at Failure:', 'diagnosisAtFailure', [
                  'Peri-implantitis', 'Failure of osseointegration', 'Allergy to material',
                  'Medication (Bisphosphonate, Chemotherapeutic, etc)', 'Radiation', 'Medical Compromise',
                  'Abnormal Loading', 'Trauma', 'Nerve injury', 'Osteomyelitis', 'Infection'
                ])}
              </div>

              {/* Upload X-rays Section */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaXRay className="size-5 text-gray-400" />
                  Upload X-rays
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Upload preplacement and removal X-rays
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="preplacementXray" className="block text-sm/6 font-medium text-gray-900">
                      Preplacement X-ray
                    </label>
                    <div className="mt-2">
                      <input
                        id="preplacementXray"
                        name="preplacementXray"
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'preplacementXray')}
                        className="block w-full text-sm text-gray-900 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="removalXray" className="block text-sm/6 font-medium text-gray-900">
                      Removal X-ray
                    </label>
                    <div className="mt-2">
                      <input
                        id="removalXray"
                        name="removalXray"
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'removalXray')}
                        className="block w-full text-sm text-gray-900 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex items-center justify-center gap-x-4 border-t border-gray-200 px-6 py-4 sm:px-10">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center gap-x-2 rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-0.5 size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <FaCheckCircle aria-hidden="true" className="-ml-0.5 size-5" />
                    <span>Submit Form</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ImplantForm;
